import React from "react";
import { Box, Button, Skeleton, Tab, Tabs, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import { FONT, NAVY } from "../../Policy/OnlinePolicy/components/formStyles";

/** The page header's second colour (its gradient runs NAVY -> NAVY_2). */
export const NAVY_2 = "#2E5A8F";
/** The Dashboard's headers: a light tint of the navbar's navy, so the page does not melt into the navbar above it. */
export const LIGHT_NAVY_BG =
  "linear-gradient(135deg, #E3EBF5 0%, #F3F6FB 60%, #FFFFFF 100%)";

/** The Dashboard's building blocks (2026-10-22 redesign): one card look for every section. */

export const CARD_SX = {
  backgroundColor: "#fff",
  border: "1px solid #EAECF0",
  borderRadius: "14px",
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  minWidth: 0,
  boxSizing: "border-box",
};

/**
 * A section: a header band in a light tint of the navbar's navy, navy title, solid navy icon - one colour family
 * (owner 2026-10-22: "not multi colour, only the header's combination", then "navbar and dashboard colour are same"
 * about solid navy headers), then its body.
 * bodyMaxHeight: long lists scroll inside the card instead of stretching the page.
 * bodySx: opt-in extra style for the body (the wallet pages use { px: 0, pb: 0 } so a wide table gets the card's full width).
 */
export function SectionCard({
  title,
  subtitle,
  icon,
  accent = NAVY,
  action,
  onAction,
  children,
  bodyMaxHeight,
  bodySx,
  testId,
  sx,
}) {
  return (
    <Box
      data-testid={testId}
      sx={{
        ...CARD_SX,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderColor: "#D5DFEC",
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: { xs: 1.75, sm: 2.25 },
          py: { xs: 1.25, sm: 1.5 },
          mb: 1.5,
          color: NAVY,
          background: LIGHT_NAVY_BG,
          borderBottom: "1px solid #D5DFEC",
        }}
      >
        {icon && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "9px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: "#fff",
              background: `linear-gradient(135deg, ${accent} 0%, ${accent === NAVY ? NAVY_2 : accent} 100%)`,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: "15px",
              color: NAVY,
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "12px",
                color: "#5B6B82",
                lineHeight: 1.4,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && onAction && (
          <Button
            size="small"
            onClick={onAction}
            endIcon={
              <ArrowForwardRoundedIcon sx={{ fontSize: "16px !important" }} />
            }
            sx={{
              flexShrink: 0,
              textTransform: "none",
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: "12.5px",
              color: NAVY,
              borderRadius: "8px",
              px: 1.25,
              backgroundColor: "#fff",
              border: "1px solid #C9D7E8",
              "&:hover": { backgroundColor: "#EEF3F9", borderColor: NAVY },
            }}
          >
            {action}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          px: { xs: 1.75, sm: 2.25 },
          pb: { xs: 1.75, sm: 2 },
          flex: 1,
          minHeight: 0,
          ...(bodyMaxHeight
            ? {
                maxHeight: bodyMaxHeight,
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehavior: "contain",
              }
            : {}),
          ...bodySx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/**
 * A headline figure with its change against the period before.
 * compact: opt-in smaller card (the wallet pages, owner 2026-11-03: "not make the cards very bigger"); the Dashboard
 * keeps the full size.
 */
export function KpiCard({
  label,
  value,
  hint,
  change,
  icon,
  accent = NAVY,
  testId,
  onClick,
  compact = false,
}) {
  const up = change && change.dir === "up";
  const down = change && change.dir === "down";
  return (
    <Box
      data-testid={testId}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      sx={{
        ...CARD_SX,
        p: compact ? { xs: 1.25, sm: 1.5 } : { xs: 1.5, sm: 2 },
        borderColor: "#D5DFEC",
        background:
          "linear-gradient(135deg, #E6EDF6 0%, #F4F7FB 55%, #fff 100%)",
        position: "relative",
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow .2s, transform .2s",
        "&:hover": onClick
          ? {
              boxShadow: "0 6px 16px rgba(16,24,40,0.08)",
              transform: "translateY(-1px)",
            }
          : {},
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          backgroundColor: accent,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: compact ? 0.25 : 0.75 }}>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: FONT,
            fontSize: compact ? "12px" : { xs: "11.5px", sm: "12.5px" },
            fontWeight: 600,
            color: "#475467",
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        {icon && (
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              width: compact ? 26 : 30,
              height: compact ? 26 : 30,
              borderRadius: "8px",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_2} 100%)`,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: compact ? { xs: "18px", sm: "20px" } : { xs: "20px", sm: "26px" },
          fontWeight: 700,
          color: "#101828",
          lineHeight: 1.15,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mt: compact ? 0.25 : 0.75,
          flexWrap: "wrap",
          minHeight: compact ? 16 : 20,
        }}
      >
        {change && (
          <Box
            data-testid={testId ? `${testId}-change` : undefined}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              px: 0.75,
              py: "1px",
              borderRadius: "999px",
              fontFamily: FONT,
              fontSize: "11.5px",
              fontWeight: 700,
              color: up ? "#067647" : down ? "#B42318" : "#475467",
              backgroundColor: up ? "#ECFDF3" : down ? "#FEF3F2" : "#F2F4F7",
            }}
          >
            {up && <TrendingUpRoundedIcon sx={{ fontSize: 14 }} />}
            {down && <TrendingDownRoundedIcon sx={{ fontSize: 14 }} />}
            {change.text}
          </Box>
        )}
        {hint && (
          <Typography
            sx={{ fontFamily: FONT, fontSize: "11.5px", color: "#667085" }}
          >
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * A section card whose header band holds tabs (owner 2026-11-03, wallet pages: "two table want separate with tab").
 * tabs: [{ value, label, count?, icon? }]; the body is the open tab's content, with no side padding so a wide table
 * uses the card's full width.
 */
export function TabbedCard({ tabs, value, onChange, children, testId, sx }) {
  return (
    <Box
      data-testid={testId}
      sx={{ ...CARD_SX, display: "flex", flexDirection: "column", overflow: "hidden", borderColor: "#D5DFEC", ...sx }}
    >
      <Box sx={{ background: LIGHT_NAVY_BG, borderBottom: "1px solid #D5DFEC", px: { xs: 1, sm: 1.5 } }}>
        <Tabs
          value={value}
          onChange={(e, next) => onChange(next)}
          variant="scrollable"
          scrollButtons={false}
          TabIndicatorProps={{ sx: { backgroundColor: NAVY, height: 3, borderRadius: "3px 3px 0 0" } }}
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              minHeight: 48,
              textTransform: "none",
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: { xs: "12.5px", sm: "13.5px" },
              color: "#5B6B82",
              px: { xs: 1, sm: 2 },
              minWidth: { xs: 0, sm: 90 },
            },
            // a phone has no room for the icons: the tabs then fit without scrolling sideways
            "& .MuiTab-iconWrapper": { display: { xs: "none", sm: "inline-flex" } },
            "& .MuiTab-root.Mui-selected": { color: NAVY },
          }}
        >
          {tabs.map((t) => (
            <Tab
              key={t.value}
              value={t.value}
              icon={t.icon}
              iconPosition="start"
              label={
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                  {t.label}
                  {typeof t.count === "number" && (
                    <Box
                      component="span"
                      sx={{
                        px: 0.75,
                        borderRadius: "999px",
                        fontSize: "11.5px",
                        fontWeight: 700,
                        lineHeight: "18px",
                        color: value === t.value ? "#fff" : NAVY,
                        backgroundColor: value === t.value ? NAVY : "#E3EBF5",
                      }}
                    >
                      {t.count}
                    </Box>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>
      {/* a table wider than the card (the Brisk ledger has 15 columns) scrolls inside it instead of being cut off */}
      <Box sx={{ minWidth: 0, overflowX: "auto" }}>{children}</Box>
    </Box>
  );
}

/** A quiet "nothing here" line inside a card. */
export const Empty = ({ children }) => (
  <Box
    sx={{
      py: 3,
      textAlign: "center",
      color: "#667085",
      fontFamily: FONT,
      fontSize: "13px",
      border: "1px dashed #EAECF0",
      borderRadius: "10px",
      backgroundColor: "#FCFCFD",
    }}
  >
    {children}
  </Box>
);

/** The page while it loads: the same grid, in grey. */
export const PageSkeleton = () => (
  <Box data-testid="dashboard-skeleton">
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0,1fr))",
          md: "repeat(4, minmax(0,1fr))",
        },
        gap: { xs: 1.25, sm: 2 },
        mb: 2,
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={112}
          sx={{ borderRadius: "14px" }}
        />
      ))}
    </Box>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0,1fr)", lg: "2fr 1fr" },
        gap: 2,
      }}
    >
      <Skeleton variant="rounded" height={320} sx={{ borderRadius: "14px" }} />
      <Skeleton variant="rounded" height={320} sx={{ borderRadius: "14px" }} />
    </Box>
  </Box>
);

/** A small "label · value" line, for lists inside cards. */
export const Row = ({ left, right, sub, onClick, testId, tone }) => (
  <Box
    data-testid={testId}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      py: 1.1,
      px: 1,
      mx: -1,
      borderRadius: "8px",
      cursor: onClick ? "pointer" : "default",
      "&:hover": onClick ? { backgroundColor: "#F9FAFB" } : {},
      "& + &": { borderTop: "1px solid #F2F4F7" },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: "13px",
          fontWeight: 600,
          color: "#101828",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {left}
      </Typography>
      {sub && (
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: "11.5px",
            color: "#667085",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {sub}
        </Typography>
      )}
    </Box>
    <Box
      sx={{
        flexShrink: 0,
        fontFamily: FONT,
        fontSize: "13px",
        fontWeight: 700,
        color: tone || "#101828",
        textAlign: "right",
      }}
    >
      {right}
    </Box>
  </Box>
);
